import React from 'react';
import { Button } from '../ui';
import { getObservationOwnerContext } from '../../utils/observationOwner';

const BOARD_STATUS_LABELS = {
  'Solicitud observada': 'Observada',
  'Oferta observada': 'Oferta obs.',
  'Pago observado': 'Pago obs.',
  'Solicitud pendiente de docu': 'Pendiente doc.',
  'Solicitud pendiente de documentación': 'Pendiente doc.',
  'Solicitud aprobada': 'Aprobada',
  'Oferta pendiente de docu': 'Pend. doc. oferta',
  'Análisis de riesgo': 'Análisis riesgo',
  'Análisis Motores': 'Análisis motores',
  'Análisis de pago': 'Análisis pago',
  'Para liquidar': 'Para liquidar',
  'Rechazo automatico': 'Rechazo auto.',
  'Rechazo analista': 'Rechazo analista',
  'Oferta desistida': 'Desistida'
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }

  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(Number(value || 0));
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const getBoardStatusLabel = (statusLabel) => {
  if (!statusLabel) return 'Sin estado';
  return BOARD_STATUS_LABELS[statusLabel] || statusLabel;
};

const getCardDateLabel = (request, isObservedColumn) => {
  if (isObservedColumn) {
    const observationLabel = request.relevant_observation?.created_at_label
      || formatDateTime(request.relevant_observation?.created_at);
    if (observationLabel) {
      return `Observada ${observationLabel}`;
    }
  }

  const updatedLabel = formatDateTime(request.updated_at);
  return updatedLabel ? `Actualizada ${updatedLabel}` : null;
};

const isPassiveColumn = (column) => column.interactionMode === 'passive';

const getPrimaryAction = ({
  request,
  column,
  canContinue,
  canComplete,
  canOpenObservations,
  isObservedColumn,
  ownerContext,
  onContinue,
  onComplete,
  onViewProfile,
  onOpenObservations
}) => {
  const resolveObservationsAsPrimary = isObservedColumn && canOpenObservations && ownerContext.isCommercialTurn;
  const viewObservationsAsPrimary = isObservedColumn && canOpenObservations && ownerContext.isLenderTurn;

  if (canContinue) {
    return {
      type: 'action',
      label: 'Continuar',
      icon: 'bi bi-arrow-right',
      handler: () => onContinue(request),
      variant: 'outlinePrimary',
      title: 'Continuar instrumentación de la solicitud',
      isPrimaryEmphasized: true,
      showObservationsSecondary: canOpenObservations
    };
  }

  if (canComplete) {
    return {
      type: 'action',
      label: 'Completar',
      icon: 'bi bi-pencil-square',
      handler: () => onComplete(request),
      variant: 'outlinePrimary',
      title: 'Completar solicitud pendiente de documentación',
      isPrimaryEmphasized: true,
      showObservationsSecondary: canOpenObservations
    };
  }

  if (resolveObservationsAsPrimary) {
    return {
      type: 'action',
      label: 'Resolver',
      icon: 'bi bi-chat-left-text',
      handler: () => onOpenObservations(request),
      variant: 'outlinePrimary',
      title: 'Resolver observación pendiente del comercio',
      isPrimaryEmphasized: true,
      showObservationsSecondary: false
    };
  }

  if (viewObservationsAsPrimary) {
    return {
      type: 'action',
      label: 'Ver',
      icon: 'bi bi-eye',
      handler: () => onOpenObservations(request),
      variant: 'ghost',
      title: 'Ver observación en seguimiento con financiera',
      isPrimaryEmphasized: false,
      showObservationsSecondary: false
    };
  }

  if (isPassiveColumn(column)) {
    return {
      type: 'passive',
      passiveLabel: column.primaryActionLabel || column.shortDescription || 'Sin acción pendiente',
      isPrimaryEmphasized: false,
      showObservationsSecondary: false
    };
  }

  const actionLabel = column.primaryActionLabel || 'Abrir';
  const actionTitle = 'Abrir perfil del cliente';

  return {
    type: 'action',
    label: actionLabel,
    icon: 'bi bi-box-arrow-up-right',
    handler: () => onViewProfile(request),
    variant: 'ghost',
    title: actionTitle,
    isPrimaryEmphasized: false,
    showObservationsSecondary: canOpenObservations
  };
};

const OriginationsBoardCard = ({
  request,
  column,
  canContinue,
  canComplete,
  canDiscard,
  canOpenObservations,
  onViewProfile,
  onContinue,
  onComplete,
  onOpenObservations,
  onDiscard
}) => {
  const amount = formatCurrency(request.offer?.amount);
  const openObservationsCount = Number(request.open_observations_count || 0);
  const hasOpenObservations = openObservationsCount > 0;
  const isObservedColumn = column.key === 'observed';
  const ownerContext = isObservedColumn
    ? getObservationOwnerContext(request.relevant_observation)
    : getObservationOwnerContext(null);
  const statusLabel = request.status_label || `Estado ${request.status}`;
  const compactStatusLabel = getBoardStatusLabel(statusLabel);
  const dateLabel = getCardDateLabel(request, isObservedColumn);
  const primaryAction = getPrimaryAction({
    request,
    column,
    canContinue,
    canComplete,
    canOpenObservations,
    isObservedColumn,
    ownerContext,
    onContinue,
    onComplete,
    onViewProfile,
    onOpenObservations
  });
  const isPassive = primaryAction.type === 'passive';
  const showObservationDot = hasOpenObservations && (
    openObservationsCount > 1 || !(isObservedColumn && ownerContext.badgeLabel)
  );
  const hasContextRow = Boolean(
    (isObservedColumn && ownerContext.badgeLabel) || amount || dateLabel
  );
  const showActionFooter = primaryAction.type === 'action' || isPassive;

  const handlePrimaryAction = () => {
    if (isPassive || !primaryAction.handler) return;
    primaryAction.handler();
  };

  return (
    <article
      className={[
        'originations-board__card',
        `originations-board__card--${column.key}`,
        primaryAction.isPrimaryEmphasized ? 'is-continuable' : '',
        isPassive ? 'is-passive' : '',
        ownerContext.cardModifier ? `is-${ownerContext.cardModifier}` : ''
      ].filter(Boolean).join(' ')}
      role={isPassive ? undefined : 'button'}
      tabIndex={isPassive ? undefined : 0}
      onClick={isPassive ? undefined : handlePrimaryAction}
      onKeyDown={isPassive ? undefined : (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlePrimaryAction();
        }
      }}
    >
      {(request.id != null || showObservationDot) && (
        <div className="originations-board__card-header">
          {request.id != null && (
            <span className="originations-board__card-id">#{request.id}</span>
          )}
          {showObservationDot && (
            <span
              className="originations-board__observation-dot"
              title={`${openObservationsCount} observación${openObservationsCount === 1 ? '' : 'es'} abierta${openObservationsCount === 1 ? '' : 's'}`}
            >
              {openObservationsCount}
            </span>
          )}
        </div>
      )}

      <div className="originations-board__card-identity">
        <h3>{request.customer || 'Cliente sin nombre'}</h3>
        <p>DNI {request.dni || 'N/A'}</p>
      </div>

      <div className="originations-board__card-status">
        <span className="originations-board__status-pill" title={statusLabel}>
          {compactStatusLabel}
        </span>
      </div>

      {hasContextRow && (
        <div className="originations-board__card-context">
          {isObservedColumn && ownerContext.badgeLabel && (
            <span
              className={`originations-board__owner-badge originations-board__owner-badge--${ownerContext.cardModifier}`}
              title={ownerContext.badgeTitle || undefined}
            >
              {ownerContext.badgeLabel}
            </span>
          )}
          {amount && (
            <span className="originations-board__card-amount">{amount}</span>
          )}
          {dateLabel && (
            <span className="originations-board__card-date">{dateLabel}</span>
          )}
        </div>
      )}

      {showActionFooter && (
        <div className="originations-board__card-footer">
          {isPassive ? (
            <span className="originations-board__card-passive-label" title={column.description}>
              {primaryAction.passiveLabel}
            </span>
          ) : (
            <Button
              variant={primaryAction.variant}
              size="sm"
              title={primaryAction.title}
              onClick={(event) => {
                event.stopPropagation();
                handlePrimaryAction();
              }}
            >
              <i className={primaryAction.icon} aria-hidden />
              {primaryAction.label}
            </Button>
          )}

          {!isPassive && primaryAction.showObservationsSecondary && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Ver observaciones"
              title="Ver observaciones"
              onClick={(event) => {
                event.stopPropagation();
                onOpenObservations(request);
              }}
            >
              <i className="bi bi-chat-left-text" aria-hidden />
              Obs.
            </Button>
          )}

          {canDiscard && (
            <button
              type="button"
              className="originations-board__icon-btn"
              aria-label="Descartar solicitud"
              title="Descartar solicitud"
              onClick={(event) => {
                event.stopPropagation();
                onDiscard(request);
              }}
            >
              <i className="bi bi-trash3" aria-hidden />
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default OriginationsBoardCard;
